import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { Subscription } from 'rxjs';

import type { RentalCancellation, RentalDecisionStamp, RentalInterruption, RentalPrescription, RentalWindowDecision } from '@app/shared/_models/sales';
import { EventType } from '@app/shared/constants-server';
import { businessDayOf } from '@app/shared/time';
import type { RentalLineView } from '../rental-billing.model';
import type { RentalDecisionTyp } from '../rental-decision-events';
import type { RentalLifecycleStore } from '../rental-lifecycle.store';
import { frDay, frShort } from '../rental-days';

export interface LineDecisionRef { typ: RentalDecisionTyp; id: number; } // décision de la ligne visée par Modifier ou Annuler

// Décision d'une ligne, en jours de Paris ; toute plage est start / end, premier et dernier jour inclus.
export type LineDecision = RentalInterruption | RentalWindowDecision | RentalPrescription;

interface Names { reason(id: number): string; color(id: number): string | null; offered(id: number): boolean; user(id: number): string; }

interface Cell { text: string; sub?: string; title?: string; dot?: string; day?: boolean; } // day : une date, jamais coupée
interface DecisionRow { ref: LineDecisionRef; cells: Cell[]; state: string; replaces: number | null; cancelNote: string | null; canModify: boolean; canCancel: boolean; }
// Tableau 'offered' : interruptions dont le motif est offert (gestes commerciaux), montrées à part comme avant la suppression du 2104.
export interface DecisionTable { key: 'interruptions' | 'offered' | 'window' | 'prescriptions'; typ: RentalDecisionTyp; title: string; heads: string[]; rows: DecisionRow[]; }

function namesOf(store: RentalLifecycleStore | null): Names {
  return {
    reason: (id) => store?.reasonName(id) ?? `motif ${id}`,
    color: (id) => store?.reasonColor(id) ?? null,
    offered: (id) => store?.reasonOffered(id) ?? false,
    user: (id) => store?.userName(id) ?? `utilisateur ${id}`,
  };
}

function stampCell(s: RentalDecisionStamp, n: Names): Cell {
  return { text: n.user(s.id_user), sub: frDay(businessDayOf(s.dat)) };
}

export function cancelNote(c: RentalCancellation | null | undefined, n: Names): string | null {
  return c ? `Annulée le ${frShort(businessDayOf(c.dat))} par ${n.user(c.id_user)} : ${c.reason}` : null;
}

function reasonCell(id: number, n: Names, fallback: string): Cell {
  return { text: n.reason(id), dot: n.color(id) ?? fallback };
}

// Tableaux par type, dans l'ordre des identifiants (chronologie de saisie) ; annulées et remplacées comprises.
export function decisionTablesOf(line: RentalLineView | null, store: RentalLifecycleStore | null, readonly: boolean): DecisionTable[] {
  const d = line?.decisions;
  if (!d) return [];
  const n = namesOf(store);
  const byId = <T extends { id: number }>(list: T[] | null | undefined) => [...(list ?? [])].sort((a, b) => a.id - b.id);
  const row = (typ: RentalDecisionTyp, x: LineDecision, cells: Cell[], modifiable: boolean): DecisionRow => ({
    ref: { typ, id: x.id },
    cells: [{ text: `n° ${x.id}` }, ...cells, stampCell(x.created, n)],
    state: x.cancelled ? 'Annulée' : 'Active',
    replaces: 'replaces' in x ? (x.replaces ?? null) : null,
    cancelNote: cancelNote(x.cancelled, n),
    canModify: modifiable && !x.cancelled && !readonly,
    canCancel: !x.cancelled && !readonly,
  });
  const tables: DecisionTable[] = [];
  // Interruptions : Du / Au inclus comme les gestes, jamais de lendemain montré (règle du 27/10/2026).
  const interruptions = byId(d.interruptions).filter((i) => !n.offered(i.reason_id));
  if (interruptions.length) {
    tables.push({
      key: 'interruptions', typ: EventType.RENTAL_INTERRUPTION, title: 'Interruptions',
      heads: ['N°', 'Du', 'Au (inclus)', 'Motif', 'Commentaire', 'Saisie'],
      rows: interruptions.map((i) => row(EventType.RENTAL_INTERRUPTION, i, [
        { text: i.start ? frDay(i.start) : 'Dès la livraison', day: !!i.start },
        i.end ? { text: frDay(i.end), day: true } : { text: 'Inconnu', title: 'Interruption ouverte : dernier jour interrompu non saisi' },
        reasonCell(i.reason_id, n, 'var(--amber)'),
        { text: i.comment ?? '' },
      ], true)),
    });
  }
  // Gestes commerciaux = interruptions au motif offert : même tableau qu'avant (Du / Au inclus), modifiables désormais (2102 replace).
  const offered = byId(d.interruptions).filter((i) => n.offered(i.reason_id));
  if (offered.length) {
    tables.push({
      key: 'offered', typ: EventType.RENTAL_INTERRUPTION, title: 'Gestes commerciaux',
      heads: ['N°', 'Du', 'Au (inclus)', 'Motif', 'Commentaire', 'Saisie'],
      rows: offered.map((i) => row(EventType.RENTAL_INTERRUPTION, i, [
        { text: i.start ? frDay(i.start) : 'Dès la livraison', day: !!i.start },
        i.end ? { text: frDay(i.end), day: true } : { text: 'Inconnu', title: 'Geste ouvert : dernier jour offert non saisi' },
        reasonCell(i.reason_id, n, 'var(--violet)'),
        { text: i.comment ?? '' },
      ], true)),
    });
  }
  // Borne END : day = dernier jour facturé (inclus), montré tel quel.
  const window = byId(d.window);
  if (window.length) {
    tables.push({
      key: 'window', typ: EventType.RENTAL_WINDOW, title: 'Début et fin de facturation',
      heads: ['N°', 'Borne', 'Jour', 'Retenue', 'Motif', 'Commentaire', 'Saisie'],
      rows: window.map((w) => {
        const effective = w.id === (w.bound === 'START' ? d.effective_start_id : d.effective_end_id);
        return row(EventType.RENTAL_WINDOW, w, [
          w.bound === 'START' ? { text: 'Début', sub: 'premier jour facturé' } : { text: 'Fin', sub: 'dernier jour facturé' },
          { text: frDay(w.day), day: true },
          effective ? { text: '✓', title: 'Borne appliquée par le serveur' } : { text: '' },
          reasonCell(w.reason_id, n, 'var(--ink-3)'),
          { text: w.comment ?? '' },
        ], false);
      }),
    });
  }
  const prescriptions = byId(d.prescriptions);
  if (prescriptions.length) {
    tables.push({
      key: 'prescriptions', typ: EventType.RENTAL_PRESCRIPTION, title: 'Prescriptions',
      heads: ['N°', 'Du', 'Au (inclus)', 'Ordonnance', 'Référence', 'Saisie'],
      rows: prescriptions.map((p) => row(EventType.RENTAL_PRESCRIPTION, p, [
        { text: frDay(p.start), day: true }, { text: frDay(p.end), day: true }, { text: frDay(p.prescribed_on), day: true }, { text: p.reference ?? '' },
      ], true)),
    });
  }
  return tables;
}

export function findDecision(line: RentalLineView | null | undefined, ref: LineDecisionRef): LineDecision | undefined {
  const d = line?.decisions;
  if (!d) return undefined;
  const list: LineDecision[] =
    ref.typ === EventType.RENTAL_INTERRUPTION ? d.interruptions
      : ref.typ === EventType.RENTAL_WINDOW ? d.window
        : ref.typ === EventType.RENTAL_PRESCRIPTION ? d.prescriptions : [];
  return (list ?? []).find((x) => x.id === ref.id);
}

// Titre et résumé du dialogue d'annulation : la décision telle que saisie, sans rien en déduire ; une interruption au motif offert se dit « geste commercial ».
export function decisionTitle(ref: LineDecisionRef, x: LineDecision | undefined, store: RentalLifecycleStore | null = null): string {
  switch (ref.typ) {
    case EventType.RENTAL_INTERRUPTION: {
      const i = x as RentalInterruption | undefined;
      return i && namesOf(store).offered(i.reason_id) ? `Annuler le geste commercial n° ${ref.id}` : `Annuler l'interruption n° ${ref.id}`;
    }
    case EventType.RENTAL_WINDOW: return (x as RentalWindowDecision | undefined)?.bound === 'START' ? `Annuler le début de facturation n° ${ref.id}` : `Annuler la fin de facturation n° ${ref.id}`;
    case EventType.RENTAL_PRESCRIPTION: return `Annuler la prescription n° ${ref.id}`;
    default: return `Annuler la décision n° ${ref.id}`;
  }
}

export function decisionSummary(ref: LineDecisionRef, x: LineDecision | undefined, store: RentalLifecycleStore | null): string {
  if (!x) return `Décision n° ${ref.id}`;
  const n = namesOf(store);
  switch (ref.typ) {
    case EventType.RENTAL_INTERRUPTION: {
      const i = x as RentalInterruption;
      return `${n.reason(i.reason_id)} · ${i.start ? 'du ' + frDay(i.start) : 'dès la livraison'}${i.end ? ' au ' + frDay(i.end) + ' inclus' : ', dernier jour inconnu'}`;
    }
    case EventType.RENTAL_WINDOW: {
      const w = x as RentalWindowDecision;
      return w.bound === 'START'
        ? `${n.reason(w.reason_id)} · premier jour facturé : ${frDay(w.day)}`
        : `${n.reason(w.reason_id)} · dernier jour facturé : ${frDay(w.day)}`;
    }
    case EventType.RENTAL_PRESCRIPTION: {
      const p = x as RentalPrescription;
      return `Prescription du ${frDay(p.start)} au ${frDay(p.end)} inclus`
        + (p.prescribed_on ? ` · ordonnance du ${frDay(p.prescribed_on)}` : '') + (p.reference ? ` · réf. ${p.reference}` : '');
    }
    default: return `Décision n° ${ref.id}`;
  }
}

// Décisions d'une ligne, annulées comprises (barrées) ; présentation seule : la fiche ouvre les dialogues (anti-cycle, C.4).
@Component({
  selector: 'app-rental-line-decisions',
  templateUrl: './rental-line-decisions.component.html',
  styleUrls: ['./rental-line-decisions.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatButtonModule],
})
export class RentalLineDecisionsComponent implements OnChanges {
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  private sub: Subscription | null = null;

  @Input() line: RentalLineView | null = null;
  @Input() store: RentalLifecycleStore | null = null; // noms des motifs et des auteurs
  @Input() readonly = false; // ligne qui ne reçoit pas de décision (cycle illisible, reprise)
  @Output() modify = new EventEmitter<LineDecisionRef>();
  @Output() cancel = new EventEmitter<LineDecisionRef>();

  tables: DecisionTable[] = [];

  constructor() {
    this.destroyRef.onDestroy(() => this.sub?.unsubscribe());
  }

  ngOnChanges(ch: SimpleChanges): void {
    if (ch['store']) {
      this.sub?.unsubscribe();
      // Noms des motifs et des auteurs arrivés après la vue : la ligne ne change pas, le rendu si.
      this.sub = this.store?.changed$?.subscribe(() => this.rebuild()) ?? null;
    }
    this.tables = decisionTablesOf(this.line, this.store, this.readonly);
  }

  private rebuild(): void {
    this.tables = decisionTablesOf(this.line, this.store, this.readonly);
    this.cdr.markForCheck();
  }
}
